/**
 * Get a clean image URL that works in both development and production
 * In development: Uses direct Supabase URLs
 * In production: Uses Netlify redirects to hide Supabase URLs
 */
export const getCustomImageUrl = (imagePath: string, _options?: {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpg' | 'png';
}): string => {
  try {
    // Handle empty or null paths
    if (!imagePath) {
      return '';
    }

    // Extract the filename and determine the bucket
    let fileName = imagePath;
    let bucket = 'article-images'; // default bucket
    
    // If it's a full Supabase URL, extract the bucket and filename
    if (imagePath.includes('/storage/v1/object/public/')) {
      const parts = imagePath.split('/storage/v1/object/public/')[1];
      if (parts) {
        const [bucketName, ...filenameParts] = parts.split('/');
        bucket = bucketName;
        fileName = filenameParts.join('/');
      }
    } else if (imagePath.includes('article-images/')) {
      fileName = imagePath.split('article-images/')[1];
      bucket = 'article-images';
    } else if (imagePath.includes('project-images/')) {
      fileName = imagePath.split('project-images/')[1];
      bucket = 'project-images';
    } else if (imagePath.includes('profile-images/')) {
      fileName = imagePath.split('profile-images/')[1];
      bucket = 'profile-images';
    } else if (imagePath.includes('/')) {
      // If it contains slashes but no bucket info, assume it's just a filename
      fileName = imagePath.split('/').pop() || imagePath;
    }

    // In development, use direct Supabase URLs
    if (import.meta.env.DEV) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      return `${supabaseUrl}/storage/v1/object/public/${bucket}/${fileName}`;
    }

    // In production, for project images and profile images use direct Supabase URLs since Netlify redirects only handle article-images
    // For article images, use Netlify redirects for clean URLs
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (bucket === 'project-images' || bucket === 'profile-images') {
      return `${supabaseUrl}/storage/v1/object/public/${bucket}/${fileName}`;
    } else {
      // Use Netlify redirect for article images
      return `/images/${fileName}`;
    }
    
  } catch (error) {
    console.error('Error in getCustomImageUrl:', error);
    // Fallback: if it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    // Otherwise construct direct Supabase URL with default bucket
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    return `${supabaseUrl}/storage/v1/object/public/article-images/${imagePath}`;
  }
};

/**
 * Legacy function - now uses Netlify CDN instead of signed URLs
 */
export const getSignedImageUrl = async (imagePath: string): Promise<string> => {
  // Use Netlify CDN instead of signed URLs for better security
  return getCustomImageUrl(imagePath);
};

/**
 * Create a proxy URL that goes through your domain
 * Format: /api/image-proxy?path=filename.jpg
 */
export const createProxyImageUrl = (imagePath: string): string => {
  const filePath = imagePath.includes('article-images/') 
    ? imagePath.split('article-images/')[1] 
    : imagePath;
  
  return `/api/image-proxy?path=${encodeURIComponent(filePath)}`;
};

/**
 * Get optimized image URL with size parameters
 */
export const getOptimizedImageUrl = (imagePath: string, width?: number, height?: number): string => {
  const filePath = imagePath.includes('article-images/') 
    ? imagePath.split('article-images/')[1] 
    : imagePath;
  
  const params = new URLSearchParams();
  params.append('path', filePath);
  if (width) params.append('w', width.toString());
  if (height) params.append('h', height.toString());
  
  return `/api/image-proxy?${params.toString()}`;
};
